// libs
import { useMemo } from "react";

// types
import {
    BOOLEAN_AS_STRING,
    CartCouponPositionXP,
    ItemImagesOnCartXPVariants,
    SelectiveCouponVariantXP,
    ValueDrivenCrossSellXP,
    PorFlowXP,
} from "@swiggy-private/cart-types";
import { ReviewedCart } from "@cart-client/interfaces/Cart";

// helpers
import { getExperimentValue, getSnaccStorefrontV2, getUserSpecificConfig } from "@cart-client/helpers/Cart";

// constants
import { EXPERIMENTS, USER_SPECIFIC_CONFIG } from "@cart-client/constants/index";

interface useCartRendererXPReturn {
    disableCancellationPopupXP: boolean;
    cartFeedbackXP: boolean;
    xpSelectiveCouponVariant: SelectiveCouponVariantXP;
    couponOrderXP: CartCouponPositionXP;
    valueDrivenCrossSellXPVariant: ValueDrivenCrossSellXP;
    itemImagesXP: ItemImagesOnCartXPVariants;
    cartAddressNudgeEnabledXp: boolean;
    porFlowXP: PorFlowXP;
    isSnaccStorefrontV2: boolean;
}

export function useCartRendererXP(cartResponse: ReviewedCart | null): useCartRendererXPReturn {
    const disableCancellationPopupXP = useMemo(
        () =>
            (getExperimentValue(cartResponse, EXPERIMENTS.CART_CANCELLATION_POPUP) as BOOLEAN_AS_STRING) ===
            BOOLEAN_AS_STRING.TRUE,
        [cartResponse],
    );

    const porFlowXP = useMemo(() => {
        const xpValue = getExperimentValue(cartResponse, EXPERIMENTS.POR_FLOW) as PorFlowXP;

        return xpValue;
    }, [cartResponse]);

    const cartFeedbackXP = useMemo(
        () =>
            (getExperimentValue(cartResponse, EXPERIMENTS.CART_FEEDBACK) as BOOLEAN_AS_STRING) ===
            BOOLEAN_AS_STRING.TRUE,
        [cartResponse],
    );

    const xpSelectiveCouponVariant = useMemo(
        () =>
            (getExperimentValue(
                cartResponse,
                EXPERIMENTS.SELECTIVE_COUPON_WIDGET_VARIANT,
            ) as SelectiveCouponVariantXP) ||
            (getUserSpecificConfig(
                cartResponse,
                USER_SPECIFIC_CONFIG.SELECTIVE_COUPON_WIDGET_VARIANT,
            ) as SelectiveCouponVariantXP),
        [cartResponse],
    );

    const couponOrderXP = useMemo(
        () => getExperimentValue(cartResponse, EXPERIMENTS.CART_APPLY_COUPON_POSITION) as CartCouponPositionXP,
        [cartResponse],
    );

    const valueDrivenCrossSellXPVariant = useMemo(() => {
        return getExperimentValue(cartResponse, EXPERIMENTS.VALUE_DRIVEN_CROSS_SELL) as ValueDrivenCrossSellXP;
    }, [cartResponse]);

    const itemImagesXP = useMemo(() => {
        return (getExperimentValue(cartResponse, EXPERIMENTS.ITEM_IMAGES) ??
            ItemImagesOnCartXPVariants.DEFAULT) as ItemImagesOnCartXPVariants;
    }, [cartResponse]);

    const cartAddressNudgeEnabledXp = useMemo(
        () =>
            (getExperimentValue(cartResponse, EXPERIMENTS.CART_ADDRESS_NUDGE_ENABLED) as BOOLEAN_AS_STRING) ===
                BOOLEAN_AS_STRING.TRUE || false,
        [cartResponse],
    );

    const isSnaccStorefrontV2 = useMemo(() => getSnaccStorefrontV2(cartResponse), [cartResponse]);

    return {
        disableCancellationPopupXP,
        cartFeedbackXP,
        xpSelectiveCouponVariant,
        couponOrderXP,
        valueDrivenCrossSellXPVariant,
        itemImagesXP,
        cartAddressNudgeEnabledXp,
        porFlowXP,
        isSnaccStorefrontV2,
    };
}
